import { canonicalTetrisNikkeKey } from './raidTetrisIdentity.js';

function textValue(value) {
  return String(value ?? '').trim();
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function strategyMemberOpenid(value = {}) {
  return textValue(value.memberOpenid ?? value.member_openid ?? value.openid);
}

export function canonicalStrategyDeck(deck = []) {
  return [...new Set((Array.isArray(deck) ? deck : []).map(canonicalTetrisNikkeKey).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right));
}

export function strategyDeckKey(deck = []) {
  return canonicalStrategyDeck(deck).join('|');
}

function actualSort(left, right) {
  const leftTime = Date.parse(left?.attackedAt ?? left?.attacked_at ?? '') || Number.MAX_SAFE_INTEGER;
  const rightTime = Date.parse(right?.attackedAt ?? right?.attacked_at ?? '') || Number.MAX_SAFE_INTEGER;
  return leftTime - rightTime
    || numberValue(left?.attackNo ?? left?.attack_no, 99) - numberValue(right?.attackNo ?? right?.attack_no, 99)
    || textValue(left?.recordKey ?? left?.record_key).localeCompare(textValue(right?.recordKey ?? right?.record_key));
}

function planMatchKey(value = {}) {
  return [
    strategyMemberOpenid(value),
    numberValue(value.bossNo ?? value.boss_no),
    numberValue(value.phaseNo ?? value.phase_no),
    strategyDeckKey(value.deck),
  ].join('::');
}

export function reconcileRaidStrategy({ plans = [], actualRecords = [] } = {}) {
  const planEntries = [];
  for (const plan of Array.isArray(plans) ? plans : []) {
    (Array.isArray(plan?.items) ? plan.items : []).forEach((item, planIndex) => {
      planEntries.push({
        ...item,
        bossNo: numberValue(item?.bossNo ?? plan?.bossNo),
        phaseNo: numberValue(item?.phaseNo ?? plan?.phaseNo),
        planIndex,
        plan,
      });
    });
  }

  const availablePlans = new Map();
  for (const entry of planEntries) {
    const key = planMatchKey(entry);
    const list = availablePlans.get(key) || [];
    list.push(entry);
    availablePlans.set(key, list);
  }

  const actualEntries = [...(Array.isArray(actualRecords) ? actualRecords : [])]
    .filter((record) => {
      const bossNo = numberValue(record?.bossNo ?? record?.boss_no);
      const phaseNo = numberValue(record?.phaseNo ?? record?.phase_no);
      return strategyMemberOpenid(record) && bossNo >= 1 && bossNo <= 5 && phaseNo >= 1 && phaseNo <= 3;
    })
    .sort(actualSort)
    .map((record) => {
      const normalized = {
        ...record,
        bossNo: numberValue(record?.bossNo ?? record?.boss_no),
        phaseNo: numberValue(record?.phaseNo ?? record?.phase_no),
        memberOpenid: strategyMemberOpenid(record),
        nickname: textValue(record?.nickname ?? record?.memberNickname ?? record?.member_nickname) || '닉네임 확인 불가',
        damageEok: numberValue(record?.damageEok ?? record?.damage_eok),
        attackNo: numberValue(record?.attackNo ?? record?.attack_no, 0) || null,
        attackedAt: record?.attackedAt ?? record?.attacked_at ?? null,
        recordKey: textValue(record?.recordKey ?? record?.record_key),
        deck: Array.isArray(record?.deck) ? record.deck : [],
      };
      const key = planMatchKey(normalized);
      const matches = availablePlans.get(key) || [];
      const matchedPlan = matches.shift() || null;
      return {
        ...normalized,
        matchedPlan,
        matched: Boolean(matchedPlan),
      };
    });

  const matchedPlanSet = new Set(actualEntries.map((entry) => entry.matchedPlan).filter(Boolean));
  const unmatchedPlans = planEntries.filter((entry) => !matchedPlanSet.has(entry));
  const unmatchedActuals = actualEntries.filter((entry) => !entry.matched);
  const effectiveAttacks = [
    ...actualEntries.map((entry) => ({ ...entry, sourceType: 'actual' })),
    ...unmatchedPlans.map((entry) => ({ ...entry, sourceType: 'plan' })),
  ];

  return {
    actualEntries,
    unmatchedActuals,
    unmatchedPlans,
    matchedPlans: [...matchedPlanSet],
    effectiveAttacks,
  };
}

export function strategyIntegrityIssues({ plans = [], actualRecords = [] } = {}) {
  const reconciled = reconcileRaidStrategy({ plans, actualRecords });
  const attacksByMember = new Map();
  for (const attack of reconciled.effectiveAttacks) {
    const openid = strategyMemberOpenid(attack);
    if (!openid) continue;
    const list = attacksByMember.get(openid) || [];
    list.push(attack);
    attacksByMember.set(openid, list);
  }

  const issues = [];
  for (const [memberOpenid, attacks] of attacksByMember.entries()) {
    const nickname = textValue(attacks.find((attack) => attack.nickname)?.nickname) || '닉네임 확인 불가';
    if (attacks.length > 3) {
      issues.push({ type: 'attack_limit', memberOpenid, nickname, count: attacks.length });
    }
    const usedByCode = new Map();
    for (const attack of attacks) {
      for (const code of canonicalStrategyDeck(attack.deck)) {
        const list = usedByCode.get(code) || [];
        list.push(attack);
        usedByCode.set(code, list);
      }
    }
    for (const [code, duplicateAttacks] of usedByCode.entries()) {
      if (duplicateAttacks.length > 1) {
        issues.push({ type: 'duplicate_nikke', memberOpenid, nickname, code, count: duplicateAttacks.length });
      }
    }
  }
  return { ...reconciled, issues };
}

export function firstStrategyIntegrityMessage(result = {}) {
  const issue = Array.isArray(result?.issues) ? result.issues[0] : null;
  if (!issue) return '';
  if (issue.type === 'attack_limit') return `${issue.nickname}은 실전과 계획을 합쳐 3타를 초과할 수 없습니다.`;
  if (issue.type === 'duplicate_nikke') return `${issue.nickname}의 3타 계획에서 같은 니케를 다시 사용할 수 없습니다.`;
  return '전략판 공격 구성을 확인해주세요.';
}

// A real attack whose deck matches a planned slot can still land far from what the practice run
// measured — a stale 모의전 record, a different overload, or a mis-entered number. The plan is only
// trustworthy if those get fixed, so the board marks them instead of quietly averaging them in.
export const STRATEGY_REVIEW_GAP_EOK = 10;
export const STRATEGY_REVIEW_GAP_RATIO = 0.08;

export function strategyActualReviewState(entry = {}) {
  const damageEok = numberValue(entry?.damageEok ?? entry?.damage_eok);
  if (!entry?.matched || !entry?.matchedPlan) {
    return { needsReview: true, reason: 'unplanned', gapEok: 0, plannedEok: 0, damageEok };
  }

  const plannedEok = numberValue(entry.matchedPlan?.damageEok);
  const gapEok = damageEok - plannedEok;
  const threshold = Math.max(STRATEGY_REVIEW_GAP_EOK, Math.abs(plannedEok) * STRATEGY_REVIEW_GAP_RATIO);
  if (plannedEok > 0 && Math.abs(gapEok) >= threshold) {
    return { needsReview: true, reason: 'damage_gap', gapEok, plannedEok, damageEok };
  }
  return { needsReview: false, reason: '', gapEok, plannedEok, damageEok };
}
